'use client';

import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
  useContext,
} from 'react';
import { navigationMenuVariants } from '@/code/frontend/components/ui/navigation-menu/navigation-menu.theme';
import { cloneElement } from '@/code/lib/utils/cloneElement';
import { cn } from '@/code/lib/utils/cn';

// Context
interface NavigationMenuContextValue {
  styles: ReturnType<typeof navigationMenuVariants>;
}

const NavigationMenuContext = createContext<NavigationMenuContextValue | null>(
  null,
);

const useNavigationMenuContext = () => {
  const context = useContext(NavigationMenuContext);
  if (!context) {
    throw new Error(
      'useNavigationMenuContext must be used within NavigationMenu',
    );
  }
  return context;
};

// Root
const NavigationMenu = ({
  className,
  children,
  ...props
}: NavigationMenuPrimitive.NavigationMenuProps & { className?: string }) => {
  const styles = navigationMenuVariants();
  return (
    <NavigationMenuContext.Provider value={{ styles }}>
      <NavigationMenuPrimitive.Root
        className={cn(styles.root(), className)}
        {...props}
      >
        {children}
      </NavigationMenuPrimitive.Root>
    </NavigationMenuContext.Provider>
  );
};

// List
const NavigationMenuList = forwardRef<
  ElementRef<typeof NavigationMenuPrimitive.List>,
  ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...props }, ref) => {
  const { styles } = useNavigationMenuContext();
  return (
    <NavigationMenuPrimitive.List
      ref={ref}
      className={cn(styles.list(), className)}
      {...props}
    />
  );
});
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

// Item (relative so content positions under it)
const NavigationMenuItem = forwardRef<
  ElementRef<typeof NavigationMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Item
    ref={ref}
    className={cn('relative', className)}
    {...props}
  />
));
NavigationMenuItem.displayName = NavigationMenuPrimitive.Item.displayName;

// Trigger (dropdown trigger with chevron)
const NavigationMenuTrigger = forwardRef<
  ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger> & {
    iconLeft?: ReactElement;
  }
>(({ className, iconLeft, children, ...props }, ref) => {
  const { styles } = useNavigationMenuContext();
  return (
    <NavigationMenuPrimitive.Trigger
      ref={ref}
      className={cn(styles.trigger(), 'group', className)}
      {...props}
    >
      {iconLeft && cloneElement({ element: iconLeft, themeStyle: styles.icon })}
      {children}
      <ChevronDown className={cn(styles.chevron())} aria-hidden="true" />
    </NavigationMenuPrimitive.Trigger>
  );
});
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

// Content (dropdown panel, positioned under its parent Item)
const NavigationMenuContent = forwardRef<
  ElementRef<typeof NavigationMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const { styles } = useNavigationMenuContext();
  return (
    <NavigationMenuPrimitive.Content
      ref={ref}
      className={cn(styles.content(), className)}
      {...props}
    >
      {children}
    </NavigationMenuPrimitive.Content>
  );
});
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

// NavLink (top-level link without dropdown)
const NavigationMenuNavLink = forwardRef<
  HTMLAnchorElement,
  {
    href: string;
    iconLeft?: ReactElement;
    badge?: ReactNode;
    className?: string;
    children: ReactNode;
    onMouseEnter?: () => void;
  }
>(({ className, href, iconLeft, badge, children, ...props }, ref) => {
  const { styles } = useNavigationMenuContext();
  return (
    <NavigationMenuPrimitive.Link asChild>
      <Link
        ref={ref}
        href={href}
        className={cn(styles.link(), className)}
        {...props}
      >
        {iconLeft &&
          cloneElement({ element: iconLeft, themeStyle: styles.icon })}
        {children}
        {badge && <span className={cn(styles.badge())}>{badge}</span>}
      </Link>
    </NavigationMenuPrimitive.Link>
  );
});
NavigationMenuNavLink.displayName = 'NavigationMenuNavLink';

// DropdownLink (item inside a dropdown content that navigates)
const NavigationMenuDropdownLink = forwardRef<
  HTMLAnchorElement,
  {
    href: string;
    iconLeft?: ReactElement;
    badge?: ReactNode;
    className?: string;
    children: ReactNode;
    onMouseEnter?: () => void;
  }
>(({ className, href, iconLeft, badge, children, ...props }, ref) => {
  const { styles } = useNavigationMenuContext();
  return (
    <NavigationMenuPrimitive.Link asChild>
      <Link
        ref={ref}
        href={href}
        className={cn(styles.item(), className)}
        {...props}
      >
        {iconLeft &&
          cloneElement({ element: iconLeft, themeStyle: styles.icon })}
        {children}
        {badge && <span className={cn(styles.badge())}>{badge}</span>}
      </Link>
    </NavigationMenuPrimitive.Link>
  );
});
NavigationMenuDropdownLink.displayName = 'NavigationMenuDropdownLink';

// Action (button inside dropdown for non-navigation actions like opening dialogs)
const NavigationMenuAction = forwardRef<
  HTMLButtonElement,
  {
    iconLeft?: ReactElement;
    badge?: ReactNode;
    children: ReactNode;
    onClick: () => void;
    className?: string;
  }
>(({ className, iconLeft, badge, children, onClick, ...props }, ref) => {
  const { styles } = useNavigationMenuContext();
  return (
    <button
      ref={ref}
      type="button"
      className={cn(styles.item(), 'w-full', className)}
      onClick={onClick}
      {...props}
    >
      {iconLeft && cloneElement({ element: iconLeft, themeStyle: styles.icon })}
      {children}
      {badge && <span className={cn(styles.badge())}>{badge}</span>}
    </button>
  );
});
NavigationMenuAction.displayName = 'NavigationMenuAction';

// Attach sub-components
NavigationMenu.List = NavigationMenuList;
NavigationMenu.Item = NavigationMenuItem;
NavigationMenu.Trigger = NavigationMenuTrigger;
NavigationMenu.Content = NavigationMenuContent;
NavigationMenu.NavLink = NavigationMenuNavLink;
NavigationMenu.DropdownLink = NavigationMenuDropdownLink;
NavigationMenu.Action = NavigationMenuAction;

export { NavigationMenu };
